import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageContent } from "./MessageContent";
import { CheckmarkIcon, PowerPointIcon, AddPromptIcon, EditIcon, SendIcon, UploadIcon } from "../../icons";
import { Colors } from "../../constants/colors";
import { Tooltip } from "../common";
import { AIPrimaryButton } from "../common/AIPrimaryButton";
import { ChatImageAttachments } from "./ChatImageAttachments";
import { ChatInput } from "./ChatInput";
import { createImageAttachmentsFromFiles, deleteContextImageById, deleteContextImagesBestEffort, isImageFile, } from "./chatImageAttachmentHelpers";
const EMPTY_USER_ATTACHMENTS = [];
const normalizeMime = (mimeType) => {
    const normalized = (mimeType || "image/png").toLowerCase();
    return normalized === "image/jpg" ? "image/jpeg" : normalized;
};
const toUserImageAttachmentRefs = (attachments) => {
    return attachments.map((attachment) => ({
        contextId: attachment.contextId,
        name: attachment.name,
        mimeType: attachment.mimeType,
        originalMimeType: attachment.originalMimeType,
        converted: attachment.converted,
        sizeBytes: attachment.sizeBytes,
    }));
};
/**
 * Renders a user message bubble (right-aligned, gray background)
 * Supports step messages with special styling for planning mode
 */
export const UserMessage = memo(({ message, onAddToPrompt, onEditMessage, fetchMentionItems, onRequestMentionUpload, uploadingFiles = [], }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [resolvedImageAttachments, setResolvedImageAttachments] = useState([]);
    const [isResolvingAttachments, setIsResolvingAttachments] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState("");
    const [editAttachments, setEditAttachments] = useState([]);
    const [editAttachmentErrors, setEditAttachmentErrors] = useState([]);
    const [isEditDragOver, setIsEditDragOver] = useState(false);
    const [isEditInputFocused, setIsEditInputFocused] = useState(false);
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
    const editInputRef = useRef(null);
    const editContainerRef = useRef(null);
    const createdEditContextIdsRef = useRef(new Set());
    const isSubmittingEditRef = useRef(false);
    const isMountedRef = useRef(true);
    const userImageAttachments = message.userImageAttachments ?? EMPTY_USER_ATTACHMENTS;
    const attachmentResolutionKey = useMemo(() => {
        return userImageAttachments
            .map((attachment) => [
            attachment.contextId,
            attachment.name,
            attachment.mimeType,
            attachment.originalMimeType || "",
            attachment.converted ? "1" : "0",
            attachment.sizeBytes ?? 0,
        ].join("|"))
            .join("||");
    }, [userImageAttachments]);
    useEffect(() => {
        let cancelled = false;
        if (userImageAttachments.length === 0) {
            setResolvedImageAttachments((prev) => (prev.length === 0 ? prev : []));
            setIsResolvingAttachments((prev) => (prev ? false : prev));
            return () => {
                cancelled = true;
            };
        }
        const resolveAttachments = async () => {
            setIsResolvingAttachments(true);
            const resolved = await Promise.all(userImageAttachments.map(async (attachment, index) => {
                const fallbackMime = normalizeMime(attachment.mimeType);
                let resolvedMime = fallbackMime;
                let previewUrl = "";
                try {
                    if (typeof window.__getContextImageData === "function") {
                        const payload = await window.__getContextImageData(attachment.contextId);
                        if (payload?.base64) {
                            resolvedMime = normalizeMime(payload.mimeType || fallbackMime);
                            previewUrl = payload.base64.startsWith("data:image/")
                                ? payload.base64
                                : `data:${resolvedMime};base64,${payload.base64}`;
                        }
                    }
                }
                catch {
                    // Keep unresolved preview blank so the UI can show an unavailable state.
                }
                return {
                    id: `${attachment.contextId}-${index}`,
                    contextId: attachment.contextId,
                    name: attachment.name,
                    mimeType: resolvedMime,
                    originalMimeType: attachment.originalMimeType,
                    converted: attachment.converted,
                    previewUrl,
                    sizeBytes: attachment.sizeBytes,
                    isUnavailable: !previewUrl,
                };
            }));
            if (!cancelled) {
                setResolvedImageAttachments(resolved);
                setIsResolvingAttachments(false);
            }
        };
        void resolveAttachments();
        return () => {
            cancelled = true;
        };
        // Depend only on serialized attachment contents; array identity can churn on rerenders.
    }, [attachmentResolutionKey]);
    const cleanupUnsubmittedEditImages = useCallback(async () => {
        if (isSubmittingEditRef.current) {
            return;
        }
        if (createdEditContextIdsRef.current.size === 0) {
            return;
        }
        await deleteContextImagesBestEffort(createdEditContextIdsRef.current);
        createdEditContextIdsRef.current.clear();
    }, []);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            void cleanupUnsubmittedEditImages();
        };
    }, [cleanupUnsubmittedEditImages]);
    const buildInitialEditAttachments = useCallback(() => {
        if (resolvedImageAttachments.length > 0 || !isResolvingAttachments) {
            return resolvedImageAttachments.map((attachment) => ({ ...attachment }));
        }
        return userImageAttachments.map((attachment, index) => ({
            id: `${attachment.contextId}-${index}`,
            contextId: attachment.contextId,
            name: attachment.name,
            mimeType: normalizeMime(attachment.mimeType),
            originalMimeType: attachment.originalMimeType,
            converted: attachment.converted,
            previewUrl: "",
            sizeBytes: attachment.sizeBytes,
            isUnavailable: true,
        }));
    }, [isResolvingAttachments, resolvedImageAttachments, userImageAttachments]);
    const startEditing = useCallback(() => {
        createdEditContextIdsRef.current.clear();
        setEditText(message.text || "");
        setEditAttachments(buildInitialEditAttachments());
        setEditAttachmentErrors([]);
        setIsEditDragOver(false);
        setIsEditInputFocused(false);
        setIsEditing(true);
        const initialText = message.text || "";
        const initialMentions = message.mentions || [];
        window.requestAnimationFrame(() => {
            editInputRef.current?.setContentWithMentions({
                text: initialText,
                mentions: initialMentions,
            });
            editInputRef.current?.focus();
        });
    }, [buildInitialEditAttachments, message.mentions, message.text]);
    const handleCancelEdit = useCallback(async () => {
        isSubmittingEditRef.current = false;
        await cleanupUnsubmittedEditImages();
        setIsSubmittingEdit(false);
        setEditAttachmentErrors([]);
        setIsEditDragOver(false);
        setIsEditInputFocused(false);
        setIsEditing(false);
        onRequestMentionUpload?.(null);
    }, [cleanupUnsubmittedEditImages, onRequestMentionUpload]);
    const appendEditImagesFromFiles = useCallback(async (files) => {
        const { attachments, errors, createdContextIds } = await createImageAttachmentsFromFiles(files, "edit_image");
        if (attachments.length > 0) {
            setEditAttachments((prev) => [...prev, ...attachments]);
        }
        if (errors.length > 0) {
            setEditAttachmentErrors((prev) => [...errors, ...prev].slice(0, 5));
        }
        createdContextIds.forEach((contextId) => createdEditContextIdsRef.current.add(contextId));
    }, []);
    const handleRemoveEditAttachment = useCallback(async (imageId) => {
        const target = editAttachments.find((attachment) => attachment.id === imageId);
        setEditAttachments((prev) => prev.filter((attachment) => attachment.id !== imageId));
        if (target?.contextId && createdEditContextIdsRef.current.has(target.contextId)) {
            createdEditContextIdsRef.current.delete(target.contextId);
            await deleteContextImageById(target.contextId);
        }
    }, [editAttachments]);
    const handleEditDrop = useCallback(async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsEditDragOver(false);
        const droppedFiles = Array.from(event.dataTransfer?.files || []).filter(isImageFile);
        if (droppedFiles.length === 0) {
            return;
        }
        await appendEditImagesFromFiles(droppedFiles);
    }, [appendEditImagesFromFiles]);
    const handleEditPasteCapture = useCallback(async (event) => {
        const clipboardItems = Array.from(event.clipboardData?.items || []);
        const pastedImageFiles = clipboardItems
            .filter((item) => item.kind === "file")
            .map((item) => item.getAsFile())
            .filter((file) => !!file && isImageFile(file));
        if (pastedImageFiles.length === 0) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        await appendEditImagesFromFiles(pastedImageFiles);
    }, [appendEditImagesFromFiles]);
    const handleEditSubmit = useCallback(async () => {
        if (!onEditMessage || isSubmittingEdit) {
            return;
        }
        const contentWithMentions = editInputRef.current?.getContentWithMentions() || {
            text: editText,
            mentions: [],
            html: "",
        };
        if (contentWithMentions.text.trim() === "" && editAttachments.length === 0) {
            return;
        }
        setIsSubmittingEdit(true);
        isSubmittingEditRef.current = true;
        let submitSucceeded = false;
        try {
            await onEditMessage({
                text: contentWithMentions.text,
                mentions: contentWithMentions.mentions,
                html: contentWithMentions.html,
                userImageAttachments: toUserImageAttachmentRefs(editAttachments),
            });
            submitSucceeded = true;
            createdEditContextIdsRef.current.clear();
            if (isMountedRef.current) {
                setEditAttachmentErrors([]);
                setIsEditDragOver(false);
                setIsEditInputFocused(false);
                setIsEditing(false);
                onRequestMentionUpload?.(null);
            }
        }
        catch {
            if (isMountedRef.current) {
                setEditAttachmentErrors((prev) => [
                    "Failed to submit your edit. Please try again.",
                    ...prev,
                ].slice(0, 5));
            }
        }
        finally {
            isSubmittingEditRef.current = false;
            if (!submitSucceeded && !isMountedRef.current && createdEditContextIdsRef.current.size > 0) {
                await deleteContextImagesBestEffort(createdEditContextIdsRef.current);
                createdEditContextIdsRef.current.clear();
            }
            if (isMountedRef.current) {
                setIsSubmittingEdit(false);
            }
        }
    }, [editAttachments, editText, isSubmittingEdit, onEditMessage, onRequestMentionUpload]);
    useEffect(() => {
        if (!isEditing) {
            return undefined;
        }
        const handlePointerDownOutside = (event) => {
            if (isSubmittingEditRef.current) {
                return;
            }
            const target = event.target;
            if (!target) {
                return;
            }
            if (editContainerRef.current?.contains(target)) {
                return;
            }
            if (target.closest(".mention-menu-container")) {
                return;
            }
            void handleCancelEdit();
        };
        document.addEventListener("mousedown", handlePointerDownOutside);
        return () => {
            document.removeEventListener("mousedown", handlePointerDownOutside);
        };
    }, [handleCancelEdit, isEditing]);
    // Step Message (special design from Figma)
    if (message.isStepMessage) {
        return (_jsx("div", { className: "flex w-full justify-end", children: _jsxs("div", { className: "max-w-[85%] bg-gray-200 border border-gray-200 rounded-lg p-2 flex flex-col gap-1", children: [_jsxs("div", { className: "flex gap-2 items-center", children: [_jsx(CheckmarkIcon, { variant: "circle", circleColor: "#10B981", checkColor: "white", size: 16 }), _jsx("span", { className: "text-[13px] text-gray-600 leading-normal", children: message.stepLabel })] }), message.stepValue && (_jsxs("div", { className: "flex items-center gap-1 border border-gray-300 rounded-full px-2 py-0.5 w-fit", children: [message.stepLabel === "Choose a style-guide" && (_jsx(PowerPointIcon, { variant: "medium", size: 16 })), _jsx("span", { className: "text-[12px] font-bold text-gray-600 leading-normal", children: message.stepValue })] })), message.text && (_jsx("p", { className: "text-[13px] text-gray-990 leading-normal mt-1", children: message.text }))] }) }));
    }
    return (_jsx("div", { className: "flex w-full justify-end", children: _jsxs("div", { className: `relative ${isEditing ? "w-full max-w-full" : "max-w-[85%] pb-4"}`, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), children: [isEditing ? (_jsxs("div", { ref: editContainerRef, className: `min-h-20 px-2 py-2 bg-gray-100 rounded-[10px] outline-1 -outline-offset-1 flex flex-col justify-start items-start gap-2 ${isEditDragOver
                        ? "outline-primary-purple-40"
                        : isEditInputFocused
                            ? "outline-primary-purple-20"
                            : "outline-gray-300"}`, onDragEnter: (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (event.dataTransfer?.types.includes("Files")) {
                            setIsEditDragOver(true);
                        }
                    }, onDragOver: (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (event.dataTransfer) {
                            event.dataTransfer.dropEffect = "copy";
                        }
                        setIsEditDragOver(true);
                    }, onDragLeave: (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!event.currentTarget.contains(event.relatedTarget)) {
                            setIsEditDragOver(false);
                        }
                    }, onDrop: (event) => {
                        void handleEditDrop(event);
                    }, onPasteCapture: (event) => {
                        void handleEditPasteCapture(event);
                    }, style: {
                        boxShadow: isEditInputFocused
                            ? "0px 0px 1px 0px rgba(0,0,0,0.04), 0px 0px 2px 0px rgba(0,0,0,0.06), 0px 4px 4px 0px rgba(0,0,0,0.06), 0px 0px 6px -4px rgba(0,0,0,0.10)"
                            : "none",
                        backgroundColor: isEditDragOver ? Colors.PrimaryPurple05 : undefined,
                    }, children: [editAttachments.length > 0 && (_jsx(ChatImageAttachments, { images: editAttachments, onRemoveImage: (imageId) => {
                                void handleRemoveEditAttachment(imageId);
                            } })), editAttachmentErrors.length > 0 && (_jsxs("div", { className: "w-full rounded-md border px-2 py-1", style: { borderColor: Colors.Red40, backgroundColor: Colors.Red10 }, children: [_jsxs("div", { className: "mb-1 flex items-center justify-between gap-2", children: [_jsx("span", { className: "text-[12px] font-bold leading-normal", style: { color: Colors.Red70 }, children: "Some images were skipped" }), _jsx("button", { type: "button", className: "border-0 bg-transparent p-0 text-[11px] font-bold cursor-pointer", style: { color: Colors.Red70 }, onClick: () => setEditAttachmentErrors([]), children: "Clear" })] }), _jsx("div", { className: "flex flex-col gap-[2px]", children: editAttachmentErrors.map((errorMessage, index) => (_jsx("p", { className: "text-[11px] leading-normal", style: { color: Colors.Red70 }, children: errorMessage }, `${errorMessage}-${index}`))) })] })), _jsx("div", { className: "self-stretch pl-1 flex justify-start items-center gap-2", children: _jsx("div", { className: "flex-1 py-0.5 relative flex justify-start items-center", children: _jsx(ChatInput, { ref: editInputRef, value: editText, onChange: setEditText, onKeyDown: (event) => {
                                        if (event.key === "Escape") {
                                            if (event.defaultPrevented || isSubmittingEditRef.current) {
                                                return;
                                            }
                                            event.preventDefault();
                                            void handleCancelEdit();
                                            return;
                                        }
                                        if (event.key === "Enter" && !event.shiftKey) {
                                            event.preventDefault();
                                            void handleEditSubmit();
                                        }
                                    }, onFocus: () => setIsEditInputFocused(true), onBlur: () => setIsEditInputFocused(false), placeholder: "Edit your message...", disabled: isSubmittingEdit, fetchMentionItems: fetchMentionItems, onUploadClick: () => {
                                        onRequestMentionUpload?.(editInputRef.current);
                                    }, uploadingFiles: uploadingFiles, sizeVariant: "compact" }) }) }), _jsxs("div", { className: "flex items-end gap-2 self-stretch w-full", children: [_jsx("div", { className: "flex items-end gap-2 flex-1", children: onRequestMentionUpload && (_jsxs("button", { type: "button", className: "inline-flex items-center justify-center gap-1 px-2.5 py-[2.5px] bg-transparent border border-gray-300 rounded-md cursor-pointer hover:bg-gray-100 transition-colors", onClick: () => {
                                            onRequestMentionUpload(editInputRef.current);
                                        }, disabled: isSubmittingEdit, "aria-label": "Upload file", children: [_jsx(UploadIcon, { size: 14, color: "#374151" }), _jsx("span", { className: "text-[11px] font-bold text-gray-800 leading-[150%]", children: "Upload File" })] })) }), _jsx(AIPrimaryButton, { onClick: () => {
                                        void handleEditSubmit();
                                    }, disabled: isSubmittingEdit || (editText.trim() === "" && editAttachments.length === 0), width: "28px", height: "28px", padding: "5px", icon: _jsx(SendIcon, { size: 16, className: "text-white" }) })] })] })) : (_jsxs("div", { className: "px-4 py-3 rounded-xl bg-gray-200 text-gray-990 wrap-break-word text-base font-normal leading-normal font-sans", children: [userImageAttachments.length > 0 && (_jsx("div", { className: message.text ? "mb-2" : "", children: isResolvingAttachments && resolvedImageAttachments.length === 0 ? (_jsx("p", { className: "text-[12px] leading-normal", style: { color: Colors.Gray600 }, children: "Loading attached images..." })) : (_jsx(ChatImageAttachments, { images: resolvedImageAttachments, readOnly: true, enableCopy: true, showLabel: false })) })), _jsx(MessageContent, { text: message.text || "", mentions: message.mentions, html: message.html, isAssistant: false, isStreaming: false })] })), isHovered && !isEditing && (_jsxs("div", { className: "absolute flex gap-1 rounded-md p-1", style: {
                        bottom: '0',
                        right: '12px',
                        backgroundColor: Colors.Gray100,
                        boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.1), 0px 1px 2px 0px rgba(0,0,0,0.06)',
                    }, children: [_jsx(Tooltip, { text: "Add to Prompt Library", children: _jsx("button", { className: "flex items-center justify-center size-6 rounded-lg border cursor-pointer overflow-hidden transition-colors", style: {
                                    backgroundColor: Colors.Gray100,
                                    borderColor: Colors.Gray100,
                                }, onMouseEnter: (e) => {
                                    e.currentTarget.style.backgroundColor = '#F3F4F6';
                                    e.currentTarget.style.borderColor = '#F3F4F6';
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.backgroundColor = Colors.Gray100;
                                    e.currentTarget.style.borderColor = Colors.Gray100;
                                }, onClick: () => {
                                    if (onAddToPrompt && message.text) {
                                        onAddToPrompt(message.text);
                                    }
                                }, "aria-label": "Add to prompts", type: "button", children: _jsx(AddPromptIcon, { size: 16, color: Colors.Gray600 }) }) }), onEditMessage && (_jsx(Tooltip, { text: "Edit", children: _jsx("button", { className: "flex items-center justify-center size-6 rounded-lg border cursor-pointer overflow-hidden transition-colors", style: {
                                    backgroundColor: Colors.Gray100,
                                    borderColor: Colors.Gray100,
                                }, onMouseEnter: (e) => {
                                    e.currentTarget.style.backgroundColor = '#F3F4F6';
                                    e.currentTarget.style.borderColor = '#F3F4F6';
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.backgroundColor = Colors.Gray100;
                                    e.currentTarget.style.borderColor = Colors.Gray100;
                                }, onClick: () => {
                                    startEditing();
                                }, "aria-label": "Edit message", type: "button", children: _jsx(EditIcon, { size: 16, color: Colors.Gray600 }) }) }))] }))] }) }));
});
UserMessage.displayName = 'UserMessage';
